<template>
	<app-modal :is-open="isOpen" :title="`Replace ${currentItem.item_code}`" @close="close">
		<div class="space-y-5">
			<div class="grid grid-cols-1 gap-3 rounded border border-gray-200 bg-gray-50 p-4 md:grid-cols-2">
				<div>
					<div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Current Item</div>
					<div class="mt-1 text-sm font-bold text-gray-900">{{ currentItem.item_code }}</div>
					<div class="text-sm text-gray-700">{{ currentItem.description }}</div>
				</div>
				<div class="grid grid-cols-3 gap-3 text-sm">
					<div>
						<div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Qty</div>
						<div class="mt-1 font-semibold text-gray-900">{{ currentItem.quantity_requested }}</div>
					</div>
					<div>
						<div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</div>
						<div class="mt-1 font-semibold text-gray-900">{{ currentItem.fulfillment_status || 'unfulfilled' }}</div>
					</div>
					<div>
						<div class="text-xs font-semibold uppercase tracking-wide text-gray-500">From Depot</div>
						<div class="mt-1 font-semibold text-gray-900">{{ currentItem.allocation_from_depot || '-' }}</div>
					</div>
				</div>
			</div>

			<form class="flex flex-wrap items-center gap-2" @submit.prevent="searchItems">
				<div class="relative min-w-[260px] flex-1">
					<input
						v-model="searchTerm"
						type="text"
						class="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-mabey-400 focus:outline-none focus:ring-2 focus:ring-mabey-400"
						placeholder="Search inspHire by item code or description..." />
					<font-awesome-icon
						icon="fas fa-search"
						class="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
				</div>
				<button
					type="submit"
					:disabled="searching || searchTerm.trim().length < 2"
					class="inline-flex items-center gap-2 rounded bg-mabey-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-mabey-700 disabled:cursor-not-allowed disabled:opacity-50">
					<font-awesome-icon v-if="searching" icon="fas fa-spinner" spin />
					<font-awesome-icon v-else icon="fas fa-search" />
					Search
				</button>
			</form>

			<div class="overflow-hidden rounded border border-gray-200 bg-white">
				<div class="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
					Search Results
				</div>
				<div v-if="searching" class="flex items-center justify-center gap-2 px-4 py-10 text-sm text-gray-500">
					<font-awesome-icon icon="fas fa-spinner" spin />
					Searching items
				</div>
				<div v-else-if="hasSearched && searchResults.length === 0" class="px-4 py-10 text-center text-sm text-gray-500">
					No matching inspHire items found
				</div>
				<div v-else-if="!hasSearched" class="px-4 py-10 text-center text-sm text-gray-500">
					Search for a replacement item
				</div>
				<div v-else class="max-h-72 overflow-y-auto divide-y divide-gray-100">
					<button
						v-for="item in searchResults"
						:key="getItemCode(item)"
						type="button"
						class="grid w-full grid-cols-1 gap-2 px-4 py-3 text-left transition-colors hover:bg-mabey-50 md:grid-cols-[minmax(120px,160px),1fr,minmax(90px,120px),minmax(80px,100px)]"
						:class="selectedItem && getItemCode(selectedItem) === getItemCode(item) ? 'bg-mabey-50 ring-1 ring-inset ring-mabey-300' : ''"
						@click="selectedItem = item">
						<div class="font-mono text-sm font-semibold text-mabey-700">{{ getItemCode(item) }}</div>
						<div>
							<div class="text-sm font-medium text-gray-900">{{ item.description || item.name || item.stockIdDescription || '-' }}</div>
							<div class="text-xs text-gray-500">{{ item.product_group || item.finish || '-' }}</div>
						</div>
						<div class="text-xs text-gray-600">
							<span v-if="item.kit" class="rounded bg-purple-100 px-1.5 py-0.5 font-semibold text-purple-700">KIT</span>
							<span v-if="item.unique" class="ml-1 rounded bg-blue-100 px-1.5 py-0.5 font-semibold text-blue-700">UNIQUE</span>
						</div>
						<div class="text-right text-xs font-semibold text-gray-700">
							{{ item.weight ? `${item.weight}kg` : '-' }}
						</div>
					</button>
				</div>
			</div>

			<div v-if="selectedItem" class="rounded border border-amber-200 bg-amber-50 p-4">
				<div class="mb-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
					<div>
						<div class="text-xs font-semibold uppercase tracking-wide text-amber-700">Replacing</div>
						<div class="mt-1 font-mono font-semibold text-gray-900">{{ currentItem.item_code }}</div>
						<div class="text-gray-700">{{ currentItem.description }}</div>
					</div>
					<div>
						<div class="text-xs font-semibold uppercase tracking-wide text-amber-700">With</div>
						<div class="mt-1 font-mono font-semibold text-gray-900">{{ getItemCode(selectedItem) }}</div>
						<div class="text-gray-700">{{ selectedItem.description || selectedItem.name || selectedItem.stockIdDescription }}</div>
					</div>
				</div>
				<label class="block text-xs font-semibold uppercase tracking-wide text-amber-700" for="replacement-reason">
					Reason
				</label>
				<textarea
					id="replacement-reason"
					v-model="reason"
					rows="3"
					class="mt-1 w-full resize-y rounded border border-amber-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
					placeholder="Why is this item being replaced?"></textarea>
				<p class="mt-2 text-xs text-amber-800">
					Allocation, target delivery, TOS and stock check fields will be reset.
				</p>
			</div>
		</div>

		<template #footer>
			<div class="flex items-center justify-end gap-3">
				<button
					type="button"
					class="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
					@click="close">
					Cancel
				</button>
				<button
					type="button"
					:disabled="!canReplace || saving"
					class="inline-flex items-center gap-2 rounded bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
					@click="replace">
					<font-awesome-icon v-if="saving" icon="fas fa-spinner" spin />
					<font-awesome-icon v-else icon="fas fa-exchange-alt" />
					Replace Item
				</button>
			</div>
		</template>
	</app-modal>
</template>

<script lang="ts" setup>
	import { computed, ref, watch } from 'vue'
	import AppModal from '@/components/base/AppModal.vue'
	import StockService from '@/services/stock'
	import type { Item } from '@/data/schema/item'
	import type { StockShortageItem } from '@/data/schema/stock-shortage'

	const props = defineProps<{
		isOpen: boolean
		currentItem: StockShortageItem
		saving?: boolean
	}>()

	const emit = defineEmits<{
		close: []
		replace: [payload: { item: Item; reason: string }]
	}>()

	const stockService = new StockService()
	const searchTerm = ref('')
	const searchResults = ref<Item[]>([])
	const selectedItem = ref<Item | null>(null)
	const reason = ref('')
	const searching = ref(false)
	const hasSearched = ref(false)

	const getItemCode = (item: Item) => item.item_code || item.code || item.item_no || (item as any).itemNo || ''

	const canReplace = computed(() => {
		return Boolean(
			selectedItem.value &&
				getItemCode(selectedItem.value) &&
				getItemCode(selectedItem.value) !== props.currentItem.item_code &&
				reason.value.trim().length > 0
		)
	})

	const extractItems = (response: any): Item[] => {
		const payload = response?.data?.data || response?.data || response
		const items = Array.isArray(payload)
			? payload
			: Array.isArray(payload?.data)
				? payload.data
				: Array.isArray(payload?.items)
					? payload.items
					: []

		return items.map((item: any) => ({
			...item,
			item_code: item.item_code || item.code || item.item_no || item.itemNo || '',
			description: item.description || item.name || item.stockIdDescription || '',
			product_group: item.product_group || item.GRPCODE || item.group || item.pgroup || '',
			kit: item.kit ?? (item.KITFLAG === '1' || item.KITFLAG === 1),
			unique: item.unique ?? (item.UNIQUE === '1' || item.UNIQUE === 1)
		}))
	}

	const itemMatchesTerm = (item: Item, term: string) => {
		const search = term.toLowerCase()
		return [
			item.item_code,
			item.code,
			item.item_no,
			(item as any).itemNo,
			item.description,
			item.name,
			item.stockIdDescription,
			item.product_group,
			item.finish
		]
			.filter(Boolean)
			.some((value) => String(value).toLowerCase().includes(search))
	}

	const uniqueItems = (items: Item[]) => {
		const seen = new Set<string>()
		return items.filter((item) => {
			const key = getItemCode(item)
			if (!key || seen.has(key)) return false
			seen.add(key)
			return true
		})
	}

	const searchReplacementItems = async (term: string) => {
		const response = await stockService.getAll({ filter: term })
		return uniqueItems(extractItems(response)).filter((item) => itemMatchesTerm(item, term))
	}

	const searchItems = async () => {
		const term = searchTerm.value.trim()
		if (term.length < 2 || searching.value) return

		searching.value = true
		hasSearched.value = true
		selectedItem.value = null
		try {
			searchResults.value = await searchReplacementItems(term)
		} catch (error) {
			console.error('Error searching replacement items:', error)
			searchResults.value = []
		} finally {
			searching.value = false
		}
	}

	const close = () => {
		emit('close')
	}

	const replace = () => {
		if (!selectedItem.value || !canReplace.value || props.saving) return
		emit('replace', {
			item: selectedItem.value,
			reason: reason.value.trim()
		})
	}

	watch(
		() => props.isOpen,
		(isOpen) => {
			if (!isOpen) return
			searchTerm.value = props.currentItem.description || props.currentItem.item_code || ''
			searchResults.value = []
			selectedItem.value = null
			reason.value = ''
			searching.value = false
			hasSearched.value = false
		},
		{ immediate: true }
	)
</script>
